import './productPricing.css';
import type { InvestmentProduct } from '@/editor/investmentProduct';
import {
  calculateInvestmentProduct,
  investmentProductCatalogText,
  investmentProductRangeVariant,
  resetInvestmentProduct,
} from '@/editor/investmentProduct';
import { formatCurrency, formatProductPrice } from '@/lib/format';
import { Button } from '@/components/ui/button';

export function ProductDisplayControls({ product, onChange }: {
  product: InvestmentProduct; onChange: (product: InvestmentProduct) => void;
}) {
  return <div className="product-display-controls">
    <div role="group" aria-label="Weergave in de offerte" className="product-display-segment">
      {(['collapsed', 'expanded'] as const).map((display) =>
        <button key={display} type="button" aria-pressed={product.display === display}
          onClick={() => onChange({ ...product, display })}>
          {display === 'collapsed' ? 'Eén regel' : 'Alle regels'}
        </button>)}
    </div>
    <label className="product-hours-toggle">
      <input type="checkbox" checked={product.showHours && product.display === 'expanded'}
        disabled={product.display !== 'expanded' || product.priceMode !== 'hours'}
        onChange={(event) => onChange({ ...product, showHours: event.target.checked })} />
      Uren × tarief tonen
    </label>
  </div>;
}

export function ProductPriceSummary({ product, currency = 'EUR' }: {
  product: InvestmentProduct; currency?: string;
}) {
  const price = calculateInvestmentProduct(product);
  const money = (cents: number | null) => cents === null ? 'Op aanvraag' : formatCurrency(cents / 100, currency);
  const differencePct = price.catalogCents ? ((price.differenceCents ?? 0) / price.catalogCents) * 100 : null;
  const ranged = price.rangeTotalCents !== null;
  return <div className="product-price-summary">
    <div><span>Catalogusprijs</span><span>{price.catalogCents === null
      ? investmentProductCatalogText(product)
      : ranged && price.rangeCatalogCents !== null
        ? formatProductPrice({ minCents: Math.min(price.catalogCents, price.rangeCatalogCents), maxCents: Math.max(price.catalogCents, price.rangeCatalogCents) }, currency)
        : money(price.catalogCents)}</span></div>
    <div className={price.adjusted ? 'product-price-adjusted' : ''}>
      <span>Aangepast voor deze offerte</span>
      <span>{!price.adjusted ? 'Gelijk aan de catalogus' : ranged ? 'Afwijkend van de catalogus' : <>
        {differencePct === null ? '' : Math.abs(differencePct).toLocaleString('nl-NL', { maximumFractionDigits: 1 }) + '% ' + (differencePct < 0 ? 'lager · ' : 'hoger · ')}
        {money(price.differenceCents)}</>}</span>
    </div>
    <div className="product-price-final"><strong>In de offerte</strong><strong>{formatProductPrice(price, currency)}</strong></div>
  </div>;
}

export default function ProductPriceControls({ product, onChange, currency = 'EUR' }: {
  product: InvestmentProduct; onChange: (product: InvestmentProduct) => void; currency?: string;
}) {
  return <div className="space-y-4">
    <div><strong>{product.productName}</strong><p className="text-xs text-muted-foreground">
      Variant {product.variants.find((variant) => variant.id === product.variantId)?.name}
      {investmentProductRangeVariant(product) && ` tot ${investmentProductRangeVariant(product)!.name}`}
    </p></div>
    <p className="text-xs font-medium">Weergave in de offerte</p>
    <ProductDisplayControls product={product} onChange={onChange} />
    <ProductPriceSummary product={product} currency={currency} />
    <Button className="w-full" onClick={() => window.dispatchEvent(new CustomEvent('open-product-pricing', { detail: product.id }))}>
      Prijsopbouw aanpassen
    </Button>
    <Button variant="outline" className="w-full" onClick={() => onChange(resetInvestmentProduct(product))}>Naar catalogusprijs</Button>
  </div>;
}
